/**
 * Matching helpers for the command palette navigation items.
 *
 * Navigation pages have short, stem-like names ("Cache", "Logs", "Jobs").
 * Users naturally type the fuller word ("caching", "logging") and expect the
 * page to still match. A plain substring check fails here because "caching"
 * does not contain "cache" (the 'e' vs 'i'). So matching combines two rules:
 *
 *   1. Substring containment in either direction (covers "cach" -> "Cache"
 *      and "Usage & Billing" matching "billing").
 *   2. A shared leading prefix of at least MIN_PREFIX_OVERLAP characters
 *      (covers "caching" -> "Cache", which share the "cach" stem).
 */

// Minimum shared leading prefix length for a fuzzy stem match. Three keeps
// short unrelated words ("c", "ca") from matching while letting real stems
// like "cach"/"log"/"work" through.
const MIN_PREFIX_OVERLAP = 3;

function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .replaceAll("[", "")
    .replaceAll("]", "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function commonPrefixLength(a: string, b: string): number {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i++;
  return i;
}

/**
 * Whether a navigation item matches the current search text.
 */
export function navigationItemMatchesSearch(
  itemName: string,
  search: string,
): boolean {
  if (!search) return true;
  const lowerName = normalizeSearchText(itemName);
  const lowerSearch = normalizeSearchText(search);
  if (!lowerSearch) return false;

  if (lowerName.includes(lowerSearch) || lowerSearch.includes(lowerName)) {
    return true;
  }

  // Shared-stem match: require the overlap to span the shorter of the two
  // strings (so "caching"/"cache" match on "cach", but "carrot"/"cat" do not
  // just because they share "ca").
  const overlap = commonPrefixLength(lowerName, lowerSearch);
  const shorter = Math.min(lowerName.length, lowerSearch.length);
  return overlap >= MIN_PREFIX_OVERLAP && overlap >= shorter - 1;
}

/**
 * Priority for Actions section items (theme switching, MCP settings, ...).
 * Actions are a small bounded set, so when the search text appears in an
 * action's name the action should outrank every unbounded result list,
 * including Codesmith session matches, whose highest priority is 100 in
 * CommandPalette. Typing "light" or "dark" must surface the appearance
 * action above the many sessions that mention those words.
 */
export function getActionItemPriority(
  itemName: string,
  search: string,
): number {
  const basePriority = 1;
  const boostPriority = 150;

  if (
    search.length > 0 &&
    itemName.toLowerCase().includes(search.toLowerCase())
  ) {
    return boostPriority;
  }

  return basePriority;
}

/**
 * Priority for navigation items based on search. Stem/prefix matches are
 * boosted so the most relevant page floats to the top (e.g. "caching" boosts
 * the "Cache" page).
 */
export function getNavigationItemPriority(
  itemName: string,
  search: string,
): number {
  const basePriority = 5;
  const boostPriority = 20;
  const exactMatchPriority = 150;

  if (search.length < 2) return basePriority;

  const lowerName = normalizeSearchText(itemName);
  const lowerSearch = normalizeSearchText(search);

  if (lowerName === lowerSearch) {
    return exactMatchPriority;
  }

  if (lowerName.startsWith(lowerSearch) || lowerSearch.startsWith(lowerName)) {
    return boostPriority;
  }

  const nameWords = lowerName.split(" ");
  const searchWords = lowerSearch.split(" ");
  if (searchWords.every((word) => nameWords.includes(word))) {
    return boostPriority;
  }

  // Also boost shared-stem matches ("caching" -> "Cache") so the stronger
  // navigation match outranks generic fallbacks.
  const overlap = commonPrefixLength(lowerName, lowerSearch);
  const shorter = Math.min(lowerName.length, lowerSearch.length);
  if (overlap >= MIN_PREFIX_OVERLAP && overlap >= shorter - 1) {
    return boostPriority;
  }

  return basePriority;
}
